import React, { useMemo } from 'react';

import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { Dialog } from 'primereact/dialog';
import { Message } from 'primereact/message';

import { withDialogFooterSpacing } from 'ui/shared/dialogFooterSpacing';

const ruleValidationDialogContentStyle: React.CSSProperties = {
  maxHeight: 'calc(100vh - 8rem)',
  overflow: 'auto'
};

interface RuleValidationError {
  errorCode: string;
  settingValue?: number;
  employees: { id: number; label: string }[];
}

interface RuleValidationErrorsDialogProps {
  visible: boolean;
  errors: RuleValidationError[];
  onHide: () => void;
}

const RULE_NAME_MAP: Record<string, string> = {
  MaxMonthlyScheduledHours:
    'Максимальное количество запланированных часов за месяц',
  MaxConsecutiveDaysWithShifts: 'Максимальное количество смен подряд'
};

export const RuleValidationErrorsDialog: React.FC<
  RuleValidationErrorsDialogProps
> = ({ visible, errors, onHide }) => {
  // Преобразуем ошибки в плоский список сотрудников с правилами
  const employeeErrors = useMemo(() => {
    const result: Array<{
      employeeId: number;
      employeeName: string;
      ruleName: string;
      ruleLimit?: string;
    }> = [];

    errors.forEach((error) => {
      const ruleName =
        RULE_NAME_MAP[error.errorCode] || 'правило планирования смен';
      const ruleLimit =
        typeof error.settingValue === 'number'
          ? ` (лимит: ${error.settingValue})`
          : '';

      error.employees.forEach((employee) => {
        result.push({
          employeeId: employee.id,
          employeeName: employee.label,
          ruleName: ruleName + ruleLimit
        });
      });
    });

    return result;
  }, [errors]);

  // Группируем по сотрудникам
  const groupedData = useMemo(() => {
    const groups = new Map<
      number,
      { employeeId: number; employeeName: string; rules: string[] }
    >();

    employeeErrors.forEach((error) => {
      if (!groups.has(error.employeeId)) {
        groups.set(error.employeeId, {
          employeeId: error.employeeId,
          employeeName: error.employeeName,
          rules: []
        });
      }
      const group = groups.get(error.employeeId)!;
      if (!group.rules.includes(error.ruleName)) {
        group.rules.push(error.ruleName);
      }
    });

    return Array.from(groups.values());
  }, [employeeErrors]);

  const employeeBodyTemplate = (rowData: {
    employeeId: number;
    employeeName: string;
    rules: string[];
  }) => {
    return (
      <div className="flex align-items-center gap-2">
        {rowData.employeeName}
      </div>
    );
  };

  const rulesBodyTemplate = (rowData: {
    employeeId: number;
    employeeName: string;
    rules: string[];
  }) => {
    return (
      <div>
        {rowData.rules.map((rule, index) => (
          <div key={index}>{rule}</div>
        ))}
      </div>
    );
  };

  return (
    <Dialog
      header="Нарушение правил планирования смен"
      visible={visible}
      className="calendarAssignments-dialog calendarAssignments-dialog--validation"
      style={{
        width: 'min(800px, calc(100vw - 1rem))',
        maxWidth: 'calc(100vw - 1rem)',
        maxHeight: 'calc(100vh - 1rem)'
      }}
      contentStyle={ruleValidationDialogContentStyle}
      onHide={onHide}
      pt={withDialogFooterSpacing()}
      footer={
        <div>
          <Button
            label="Закрыть"
            className="p-button-secondary"
            onClick={onHide}
          />
        </div>
      }
    >
      <Message
        severity="warn"
        text="График пересчитан частично. Смены для следующих сотрудников не могут быть спланированы из-за нарушения правил."
      />
      <DataTable
        value={groupedData}
        scrollable
        scrollHeight="400px"
        className="w-full"
      >
        <Column
          field="employeeName"
          header="Сотрудник"
          body={employeeBodyTemplate}
        />
        <Column
          field="rules"
          header="Нарушенные правила"
          body={rulesBodyTemplate}
        />
      </DataTable>
    </Dialog>
  );
};
